import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as Icons from "@/assets/features/sidebar";
import SelectLanguages from "../shared/inputs/SelectLanguages";
import { Avatar } from "../shared/avatar/Avatar";
import Dropdown from "../shared/inputs/DropDown";
import InputField from "../shared/inputs/InputField";
import ThemeToggle from "../shared/inputs/ThemeToggle";
import SelectInput from "../shared/inputs/SelectInput";
import {
  useIsIncludeCompany,
  useLogout,
  useSetToken,
  useUser,
} from "@/stores/authStore";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmationModal } from "../shared/modals/ConfirmationModal";
import { refreshToken } from "@/api/comman/commanApi";
import { toastError } from "@/lib/Toast";

const Navbar = () => {
  const { t } = useTranslation();
  const logout = useLogout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isIncludeCompany = useIsIncludeCompany();
  const setToken = useSetToken();
  const user = useUser();

  const [selectedCompany, setSelectedCompany] = useState(
    user?.companyId || null
  );

  const { mutate: refreshTokenMutate, isPending } = refreshToken();

  useEffect(() => {
    if (user?.companyId !== selectedCompany) {
      setSelectedCompany(user?.companyId);
    }
  }, [user?.companyId]);

  const handleLogout = () => {
    queryClient.removeQueries({ queryKey: ["user-info"] });
    queryClient.cancelQueries({ queryKey: ["user-info"] });
    logout();
    navigate("/auth/login");
  };

  const handleDropdownSelect = (value) => {
    if (value === "logout") {
      handleLogout();
    }
    if (value === "profile") {
      navigate("/profile?tab=user");
    }
  };

  const handleSwitchCompany = (val) => {
    setSelectedCompany(val);

    refreshTokenMutate(
      { company_id: `${val}` },
      {
        onSuccess: (response) => {
          const token = response?.data?.accessToken;
          if (token) {
            setToken(token);
            queryClient.invalidateQueries();
            navigate("/");
          }
        },
        onError: () => {
          setSelectedCompany(user?.companyId || null);
          toastError({
            header: t("navbar.companySwitchFailedHeader"),
            description: t("navbar.companySwitchFailedDescription"),
          });
        },
      }
    );
  };

  return (
    <div className="flex justify-between items-center font-[Dubai] mb-[2rem]">
      {!isIncludeCompany && (
        <img src={Icons.FullLogo} className="h-20 w-45 relative right-5" />
      )}

      {isIncludeCompany && (
        <SelectInput
          className="!rounded-full bg-white border-none"
          placeholder={t("navbar.selectAgencyPlaceholder")}
          options={user?.companies?.map((company) => ({
            label: company?.label,
            value: company?.value,
            logo: company?.logo,
          }))}
          value={selectedCompany}
          onChange={handleSwitchCompany}
        
        />
      )}

      <div className="flex items-center gap-3">
        {isIncludeCompany && (
          <div className="relative px-4 pb-4 w-fit mt-4 left-3">
            <InputField
              className="bg-[#00000009] w-40 rounded-full border-none py-2 pl-10 pr-3 text-sm"
              placeholder={t("navbar.searchPlaceholder")}
            />
            <Icons.Search
              color="#909FBA"
              className="h-4 w-4 absolute left-8 top-2.5 text-gray-500"
            />
          </div>
        )}

        <SelectLanguages />

        {isIncludeCompany && (
          <>
            <Icons.Message height={18} width={18} />
            <Icons.Notification height={18} width={18} />
          </>
        )}

        <Dropdown
          label={
            <div className="flex items-center gap-1.5 cursor-pointer">
              <Avatar
                name={user?.fullname || t("navbar.user")}
                size="sm"
                className="w-[2.875rem] h-[2.875rem]"
              />
              <button className="flex flex-col items-start text-sm"
              
              >
                <span>{user?.fullname || t("navbar.user")}</span>
                <span className="text-[var(--color-primary-700)] font-medium text-sm">
                  {user?.userTitle}
                </span>
              </button>
              <div className="bg-[var(--color-babyBlue-900)] rotate-270 rounded-full p-1 ml-5">
                <Icons.Arrow color="white" className="h-3 w-3" />
              </div>
            </div>
          }
          items={
            isIncludeCompany
              ? [
                  { label: t("navbar.myProfile"),
                     action: "profile"},
                  { label: <ThemeToggle />, action: "theme" },
                  { separator: true },
                  { label: t("navbar.logout"), action: "logout" },
                ]
              : [{ label: t("navbar.logout"), action: "logout" }]
          }
          onSelect={(item) => handleDropdownSelect(item.action)}
          className="px-0"
        />
      </div>

      <ConfirmationModal
        open={isPending}
        showCancel={false}
        onOpenChange={(open) => {}}
        icon={
          <div className="bg-primary p-5 rounded-full">
            <img src={Icons.Switch} />
          </div>
        }
        title={t("navbar.switchCompanyTitle")}
        description={
          <div className="flex flex-col justify-center items-center">
            {t("navbar.switchCompanyProgress")}
            <div className="flex items-center gap-5 mt-7">
              <div className="rounded-[20px] border border-[#F0F0F0] p-3">
                <img src={Icons.Logo} className="h-13 w-13" />
              </div>
              <img src={Icons.Switch2} />
              <div className="rounded-[20px] border border-[#F0F0F0] p-3">
                <img src={Icons.Logo} className="h-13 w-13" />
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default Navbar;
